<script setup>
/** 顶栏右上角的通知收件箱：所有等我处理的待办和审批汇在一起，铃铛上的数字是条数。 */
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { api, selectGuild } from '../api.js'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const panel = ref(null)
const items = ref([])
const count = ref(0)
const error = ref('')
let timer

async function load() {
  try {
    const data = await api('GET', '/api/voko/inbox')
    items.value = data.items
    count.value = data.count
    error.value = ''
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
}

onMounted(() => {
  load()
  timer = setInterval(load, 60_000)
})
onBeforeUnmount(() => clearInterval(timer))
// 换页后重取：刚在某个页面批掉的事，数字马上少一条。
watch(() => route.path, load)

const when = (/** @type {string} */ iso) => new Date(iso).toLocaleString(locale.value, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })

async function open(item) {
  panel.value.open = false
  if (item.guild_slug !== null) selectGuild(item.guild_slug)
  await router.push(item.to)
}
</script>

<template>
  <details ref="panel" class="inbox" @toggle="panel.open && load()">
    <summary :aria-label="t('inbox.title', { count })" :title="t('inbox.title', { count })">
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" /></svg>
      <span v-if="count > 0" class="inbox__badge">{{ count > 99 ? '99+' : count }}</span>
    </summary>
    <div class="inbox__panel">
      <p class="inbox__head">{{ t('inbox.title', { count }) }}</p>
      <p v-if="error" role="alert" class="inbox__empty">{{ error }}</p>
      <p v-else-if="items.length === 0" class="inbox__empty">{{ t('inbox.empty') }}</p>
      <ul v-else>
        <li v-for="item in items" :key="item.id">
          <button type="button" @click="open(item)">
            <span class="inbox__kind">{{ t(`inbox.kinds.${item.kind}`) }}</span>
            <span class="inbox__text">{{ t(`inbox.text.${item.kind}`, item.data) }}</span>
            <time class="inbox__time" :datetime="item.created_at">{{ when(item.created_at) }}</time>
          </button>
        </li>
      </ul>
    </div>
  </details>
</template>

<style scoped>
.inbox {
  position: relative;
  margin-left: auto;
}
.inbox summary {
  position: relative;
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: var(--drama-radius-full);
  color: var(--drama-color-text);
  cursor: pointer;
  list-style: none;
}
.inbox summary::-webkit-details-marker {
  display: none;
}
.inbox summary:hover {
  background: color-mix(in srgb, var(--drama-color-text) 6%, transparent);
}
.inbox__badge {
  position: absolute;
  top: 0;
  right: 0;
  min-width: 18px;
  padding: 0 5px;
  border-radius: var(--drama-radius-full);
  background: var(--drama-color-danger);
  color: var(--drama-color-bg);
  font-size: var(--drama-font-size-xs);
  font-weight: var(--drama-font-weight-bold);
  line-height: 18px;
  text-align: center;
}
.inbox__panel {
  position: absolute;
  right: 0;
  top: calc(100% + var(--drama-space-xs));
  z-index: 40;
  width: min(380px, calc(100vw - 2 * var(--drama-space-md)));
  max-height: 70vh;
  overflow-y: auto;
  padding: var(--drama-space-xs);
  background: var(--drama-color-surface);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  box-shadow: var(--drama-shadow-lg);
}
.inbox__head {
  margin: 0;
  padding: var(--drama-space-sm) var(--drama-space-md);
  font-weight: var(--drama-font-weight-bold);
  border-bottom: 1px solid var(--drama-color-border);
}
.inbox__empty {
  margin: 0;
  padding: var(--drama-space-md);
  color: var(--drama-color-text-muted);
}
.inbox ul {
  margin: 0;
  padding: 0;
  list-style: none;
}
.inbox li button {
  display: grid;
  gap: 2px;
  width: 100%;
  padding: var(--drama-space-sm) var(--drama-space-md);
  text-align: left;
  background: transparent;
  border: 0;
  border-radius: var(--drama-radius-sm);
  color: var(--drama-color-text);
  cursor: pointer;
}
.inbox li button:hover {
  background: color-mix(in srgb, var(--drama-color-text) 6%, transparent);
}
.inbox__kind {
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-primary);
  font-weight: var(--drama-font-weight-medium);
}
.inbox__text {
  font-size: var(--drama-font-size-sm);
  overflow-wrap: anywhere;
}
.inbox__time {
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
}
</style>
